import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Users,
  TrendingUp,
  Phone,
  CheckCircle,
  Sparkles,
  MapPin,
  ArrowRight,
  Target,
  UserCheck,
  Clock,
} from 'lucide-react';
import { useLeads, useLeadStats } from '../hooks/useLeads';
import { useRole } from '../contexts/RoleContext';
import { RequirePermission } from '../components/auth/RoleGuard';
import { Badge } from '../components/common/Badge';
import { SLASummaryCard } from '../components/dashboard/SLASummaryCard';
import type { Lead } from '../api/client';

function StatCard({
  title,
  value,
  icon: Icon,
  color = 'text-font-primary',
  bgColor = 'bg-white',
  subtitle,
  link,
}: {
  title: string;
  value: string | number;
  icon: typeof Users;
  color?: string;
  bgColor?: string;
  subtitle?: string;
  link?: string;
}) {
  const content = (
    <div className={`card p-5 ${bgColor} ${link ? 'hover:shadow-md transition-shadow cursor-pointer' : ''}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-font-secondary">{title}</p>
          <p className={`text-3xl font-serif font-semibold mt-1 ${color}`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-font-secondary mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg ${bgColor === 'bg-white' ? 'bg-gray-100' : 'bg-white/20'}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
      {link && (
        <div className="mt-3 pt-3 border-t border-card-border flex items-center text-sm text-brand-primary">
          View details <ArrowRight className="w-4 h-4 ml-1" />
        </div>
      )}
    </div>
  );

  if (link) {
    return <Link to={link}>{content}</Link>;
  }
  return content;
}

function RecentLeadRow({ lead }: { lead: Lead }) {
  return (
    <Link
      to={`/leads/${lead.id}`}
      className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 rounded-lg transition-colors"
    >
      <div className="flex items-center gap-3">
        {lead.is_new && (
          <Sparkles className="w-4 h-4 text-brand-secondary flex-shrink-0" />
        )}
        <div>
          <p className="font-medium text-font-primary">{lead.name}</p>
          <p className="text-sm text-font-secondary">
            {lead.city}, {lead.region}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {lead.cuisine_detected && (
          <Badge variant="default" className="text-xs">
            {lead.cuisine_detected}
          </Badge>
        )}
        <Badge variant="status" statusType={lead.status}>
          {lead.status}
        </Badge>
      </div>
    </Link>
  );
}

function DashboardContent() {
  const { can, role } = useRole();
  const { data: stats, isLoading: statsLoading } = useLeadStats();
  const { data: recentLeads, isLoading: leadsLoading } = useLeads({
    page: 1,
    limit: 5,
    order: 'desc',
    sort: 'first_seen_at',
    is_new: true,
  });

  const canViewAssignments = can('leads:view_assignments');
  const canClaim = can('leads:claim');
  const isSalesManager = role === 'sales_manager' || role === 'superadmin';

  // Calculate conversion rate
  const conversionRate = useMemo(() => {
    if (!stats || stats.total === 0) return 0;
    return Math.round((stats.status_qualified / stats.total) * 100);
  }, [stats]);

  // Get top regions by new leads
  const topRegions = useMemo(() => {
    if (!stats?.by_region) return [];
    return [...stats.by_region]
      .sort((a, b) => b.new_count - a.new_count)
      .slice(0, 5);
  }, [stats]);

  // Get top cuisines
  const topCuisines = useMemo(() => {
    if (!stats?.by_cuisine) return [];
    return [...stats.by_cuisine]
      .filter(c => c.cuisine)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [stats]);

  const isLoading = statsLoading || leadsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-r-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-serif font-semibold text-font-primary">Dashboard</h1>
        <p className="text-font-secondary mt-1">
          {isSalesManager
            ? 'Overview of sales pipeline and team performance'
            : canClaim
            ? 'Your sales pipeline at a glance'
            : 'Lead metrics overview'}
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Leads"
          value={stats?.total ?? 0}
          icon={Users}
          link="/leads"
        />
        <StatCard
          title="New This Week"
          value={stats?.new_count ?? 0}
          icon={Sparkles}
          color="text-brand-secondary"
          subtitle={`${stats?.total ? Math.round((stats.new_count / stats.total) * 100) : 0}% of total`}
          link="/leads?new=true"
        />
        <StatCard
          title="Contacted"
          value={stats?.status_contacted ?? 0}
          icon={Phone}
          color="text-blue-600"
          subtitle="In progress"
        />
        <StatCard
          title="Qualified"
          value={stats?.status_qualified ?? 0}
          icon={CheckCircle}
          color="text-green-600"
          subtitle={`${conversionRate}% conversion`}
        />
      </div>

      {/* Sales Pipeline - for sales roles */}
      {canClaim && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card p-5 bg-gradient-to-br from-brand-primary to-brand-bg text-white">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5" />
              <h3 className="font-semibold">Pipeline Status</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-white/80">New</span>
                <span className="font-semibold">{stats?.status_new ?? 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/80">Contacted</span>
                <span className="font-semibold">{stats?.status_contacted ?? 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/80">Qualified</span>
                <span className="font-semibold">{stats?.status_qualified ?? 0}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/80">Closed</span>
                <span className="font-semibold">{stats?.status_closed ?? 0}</span>
              </div>
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-brand-primary" />
              <h3 className="font-semibold text-font-primary">Conversion Rate</h3>
            </div>
            <div className="text-center py-4">
              <p className="text-4xl font-serif font-bold text-brand-primary">
                {conversionRate}%
              </p>
              <p className="text-sm text-font-secondary mt-1">
                leads qualified
              </p>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className="bg-brand-primary rounded-full h-2 transition-all"
                style={{ width: `${Math.min(conversionRate, 100)}%` }}
              />
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-yellow-600" />
              <h3 className="font-semibold text-font-primary">Quick Actions</h3>
            </div>
            <div className="space-y-2">
              <Link
                to="/leads?new=true"
                className="block p-3 rounded-lg bg-yellow-50 hover:bg-yellow-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-yellow-800">
                    Review new leads
                  </span>
                  <Badge variant="warning">{stats?.new_count ?? 0}</Badge>
                </div>
              </Link>
              <Link
                to="/leads?status=contacted"
                className="block p-3 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800">
                    Follow up contacted
                  </span>
                  <Badge variant="info">{stats?.status_contacted ?? 0}</Badge>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Manager View - Team Stats */}
      {isSalesManager && canViewAssignments && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 card p-5">
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="w-5 h-5 text-brand-primary" />
              <h3 className="font-semibold text-font-primary">Team Overview</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-2xl font-semibold text-font-primary">{stats?.total ?? 0}</p>
                <p className="text-sm text-font-secondary">Total in Pipeline</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-2xl font-semibold text-green-600">{stats?.status_qualified ?? 0}</p>
                <p className="text-sm text-font-secondary">Qualified</p>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-2xl font-semibold text-blue-600">{stats?.status_contacted ?? 0}</p>
                <p className="text-sm text-font-secondary">In Contact</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-2xl font-semibold text-yellow-600">{stats?.new_count ?? 0}</p>
                <p className="text-sm text-font-secondary">Awaiting Review</p>
              </div>
            </div>
          </div>
          <SLASummaryCard />
        </div>
      )}

      {/* Bottom Row - Regional & Cuisine breakdown + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Regional breakdown */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-brand-primary" />
            <h3 className="font-semibold text-font-primary">By Region</h3>
          </div>
          <div className="space-y-3">
            {topRegions.map((region) => (
              <div key={region.region} className="flex items-center justify-between">
                <span className="text-sm text-font-secondary">{region.region}</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{region.total}</span>
                  {region.new_count > 0 && (
                    <Badge variant="success" className="text-xs">
                      +{region.new_count}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
            {topRegions.length === 0 && (
              <p className="text-sm text-font-secondary">No regional data</p>
            )}
          </div>
        </div>

        {/* Cuisine breakdown */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-brand-primary" />
            <h3 className="font-semibold text-font-primary">Top Cuisines</h3>
          </div>
          <div className="space-y-3">
            {topCuisines.map((cuisine) => (
              <div key={cuisine.cuisine} className="flex items-center justify-between">
                <span className="text-sm text-font-secondary">{cuisine.cuisine}</span>
                <span className="text-sm font-medium">{cuisine.total}</span>
              </div>
            ))}
            {topCuisines.length === 0 && (
              <p className="text-sm text-font-secondary">No cuisine data</p>
            )}
          </div>
        </div>

        {/* Recent New Leads */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand-secondary" />
              <h3 className="font-semibold text-font-primary">Recent New Leads</h3>
            </div>
            <Link to="/leads?new=true" className="text-sm text-brand-primary hover:underline">
              View all
            </Link>
          </div>
          <div className="space-y-1">
            {recentLeads?.leads?.slice(0, 5).map((lead) => (
              <RecentLeadRow key={lead.id} lead={lead} />
            ))}
            {(!recentLeads?.leads || recentLeads.leads.length === 0) && (
              <p className="text-sm text-font-secondary py-4 text-center">
                No new leads this week
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function Dashboard() {
  return (
    <RequirePermission permission="dashboard:view">
      <DashboardContent />
    </RequirePermission>
  );
}
